import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { eventsAPI, eventSeriesAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  History, Plus, Link2, Unlink, Star, DollarSign, MapPin, Calendar,
  TrendingUp, TrendingDown, Minus, ChevronRight, Loader2, Package,
  FileText, ExternalLink, Edit
} from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

export const SeriesHistoryPanel = ({ eventId, eventTitle, eventCity, eventState, onSeriesLinked, fullWidth = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [seriesHistory, setSeriesHistory] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState('');
  const [linking, setLinking] = useState(false);
  const [userSeries, setUserSeries] = useState([]);
  const [selectedForUnlink, setSelectedForUnlink] = useState([]);

  useEffect(() => {
    loadSeriesHistory();
  }, [eventId]);

  const loadSeriesHistory = async () => {
    setLoading(true);
    try {
      // Get series history for this event
      const historyRes = await eventsAPI.getSeriesHistory(eventId);
      
      if (historyRes.data.has_series) {
        setSeriesHistory(historyRes.data.series);
      } else {
        setSeriesHistory(null);
        // Get suggestions
        const suggestRes = await eventsAPI.suggestSeries(eventId);
        setSuggestions([
          ...(suggestRes.data.exact_match ? [{ ...suggestRes.data.exact_match, isExact: true }] : []),
          ...suggestRes.data.suggestions
        ]);
      }
    } catch (error) {
      console.error('Failed to load series history:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserSeries = async () => {
    try {
      const res = await eventSeriesAPI.getAll();
      setUserSeries(res.data);
    } catch (error) {
      console.error('Failed to load series:', error);
    }
  };

  const handleOpenLinkDialog = () => {
    loadUserSeries();
    setShowLinkDialog(true);
  };

  const handleLinkToSeries = async (seriesId) => {
    setLinking(true);
    try {
      await eventsAPI.linkToSeries(eventId, seriesId);
      toast.success('Event linked to series!');
      setShowLinkDialog(false);
      loadSeriesHistory();
      if (onSeriesLinked) onSeriesLinked();
    } catch (error) {
      toast.error('Failed to link event to series');
    } finally {
      setLinking(false);
    }
  };

  const handleCreateSeries = async () => {
    if (!newSeriesName.trim()) {
      toast.error('Please enter a series name');
      return;
    }
    
    setLinking(true);
    try {
      // Create the series
      const createRes = await eventSeriesAPI.create({ name: newSeriesName.trim() });
      const newSeriesId = createRes.data.id;
      
      // Link the event to the new series
      await eventsAPI.linkToSeries(eventId, newSeriesId);
      
      toast.success(`Created series "${newSeriesName}" and linked event!`);
      setShowCreateDialog(false);
      setNewSeriesName('');
      loadSeriesHistory();
      if (onSeriesLinked) onSeriesLinked();
    } catch (error) {
      toast.error('Failed to create series');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkFromSeries = async () => {
    setLinking(true);
    try {
      await eventsAPI.unlinkFromSeries(eventId);
      toast.success('Event unlinked from series');
      loadSeriesHistory();
      if (onSeriesLinked) onSeriesLinked();
    } catch (error) {
      toast.error('Failed to unlink event');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkSelected = async () => {
    if (selectedForUnlink.length === 0) return;
    
    setLinking(true);
    try {
      // Unlink each selected event
      for (const eventIdToUnlink of selectedForUnlink) {
        await eventsAPI.unlinkFromSeries(eventIdToUnlink);
      }
      toast.success(`Unlinked ${selectedForUnlink.length} event${selectedForUnlink.length > 1 ? 's' : ''} from series`);
      setSelectedForUnlink([]);
      loadSeriesHistory();
      if (onSeriesLinked) onSeriesLinked();
    } catch (error) {
      toast.error('Failed to unlink events');
    } finally {
      setLinking(false);
    }
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-gray-400 text-sm">Not rated</span>;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const getSalesTrend = (occurrences) => {
    if (occurrences.length < 2) return null;
    const sorted = [...occurrences].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const latest = sorted[sorted.length - 1]?.sales_total || 0;
    const previous = sorted[sorted.length - 2]?.sales_total || 0;
    if (previous === 0) return null;
    const change = ((latest - previous) / previous) * 100;
    return { change, isUp: change > 0 };
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl border-2 border-[#1E3A5F] p-6 ${fullWidth ? 'shadow-hard' : ''}`}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
        </div>
      </div>
    );
  }

  // If event has a series - show the full history
  if (seriesHistory) {
    const allOccurrences = seriesHistory.occurrences;
    
    // Calculate aggregate stats from occurrences
    const totalBoothCost = allOccurrences.reduce((sum, occ) => sum + (occ.booth_cost || 0), 0);
    const avgBoothCost = allOccurrences.length > 0 ? totalBoothCost / allOccurrences.length : 0;
    const ratings = allOccurrences.map(o => o.event_rating).filter(Boolean);
    const avgRating = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
    
    // Calculate average ROI: (total_sales - booth_cost) / booth_cost * 100
    const roiValues = allOccurrences
      .filter(occ => (occ.booth_cost || 0) > 0)
      .map(occ => ((occ.sales_total || 0) - (occ.booth_cost || 0)) / (occ.booth_cost || 1) * 100);
    const avgROI = roiValues.length > 0 ? roiValues.reduce((a, b) => a + b, 0) / roiValues.length : null;

    return (
      <div className={`bg-white rounded-2xl border-2 border-[#1E3A5F] p-6 ${fullWidth ? 'shadow-hard' : ''}`} data-testid="series-history-panel">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`${fullWidth ? 'w-14 h-14' : 'w-10 h-10'} rounded-xl bg-[#1E3A5F]/10 flex items-center justify-center`}>
              <History className={`${fullWidth ? 'w-7 h-7' : 'w-5 h-5'} text-[#1E3A5F]`} />
            </div>
            <div>
              <h3 className={`${fullWidth ? 'text-2xl' : 'text-lg'} font-bold text-[#1E3A5F]`}>{seriesHistory.name}</h3>
              <p className="text-sm text-gray-500">{seriesHistory.years_attended} year{seriesHistory.years_attended !== 1 ? 's' : ''} attended</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/sales?series_id=${seriesHistory.id}&event_id=${eventId}&channel=event`}>
              <Button variant="outline" className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/10" data-testid="add-sales-record-btn">
                <DollarSign className="w-4 h-4 mr-2" />
                Add Sales Record
              </Button>
            </Link>
            <Link to={`/add-event?series=${seriesHistory.id}&prefill=${eventId}`}>
              <Button className="bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white" data-testid="add-event-in-series-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add New Event in Series
              </Button>
            </Link>
          </div>
        </div>

        {/* Aggregate Stats - Expanded for full width */}
        <div className={`grid ${fullWidth ? 'grid-cols-4 md:grid-cols-6' : 'grid-cols-3'} gap-4 mb-6 p-4 bg-[#1E3A5F]/5 rounded-xl`}>
          <div className="text-center">
            <p className={`${fullWidth ? 'text-3xl' : 'text-2xl'} font-bold text-[#1E3A5F]`}>{formatCurrency(seriesHistory.total_sales)}</p>
            <p className="text-xs text-gray-500">Total Sales</p>
          </div>
          <div className="text-center">
            <p className={`${fullWidth ? 'text-3xl' : 'text-2xl'} font-bold text-[#1E3A5F]`}>{formatCurrency(seriesHistory.avg_sales)}</p>
            <p className="text-xs text-gray-500">Avg per Year</p>
          </div>
          <div className="text-center flex flex-col items-center">
            {avgROI !== null ? (
              <>
                <div className={`flex items-center gap-1 ${fullWidth ? 'text-3xl' : 'text-2xl'} font-bold ${avgROI >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {avgROI >= 0 ? <TrendingUp className={`${fullWidth ? 'w-6 h-6' : 'w-5 h-5'}`} /> : <TrendingDown className={`${fullWidth ? 'w-6 h-6' : 'w-5 h-5'}`} />}
                  {Math.abs(avgROI).toFixed(0)}%
                </div>
                <p className="text-xs text-gray-500">Avg ROI</p>
              </>
            ) : (
              <>
                <Minus className="w-5 h-5 text-gray-400" />
                <p className="text-xs text-gray-500">No ROI Data</p>
              </>
            )}
          </div>
          {fullWidth && (
            <>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#1E3A5F]">{seriesHistory.years_attended}</p>
                <p className="text-xs text-gray-500">Years Attended</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#1E3A5F]">{formatCurrency(avgBoothCost)}</p>
                <p className="text-xs text-gray-500">Avg Booth Cost</p>
              </div>
              <div className="text-center">
                {avgRating ? (
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-3xl font-bold text-[#1E3A5F]">{avgRating.toFixed(1)}</p>
                    <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-gray-300">—</p>
                )}
                <p className="text-xs text-gray-500">Avg Rating</p>
              </div>
            </>
          )}
        </div>

        {/* All Occurrences - Full width table view */}
        {fullWidth ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-[#1E3A5F] flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Sales History ({allOccurrences.length})
              </h4>
              {selectedForUnlink.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUnlinkSelected}
                  disabled={linking}
                  className="border-red-500 text-red-500 hover:bg-red-50"
                  data-testid="unlink-selected-btn"
                >
                  <Unlink className="w-4 h-4 mr-1" />
                  Unlink Selected ({selectedForUnlink.length})
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-[#1E3A5F]/20">
                    <th className="text-center py-3 px-2 w-10">
                      <input
                        type="checkbox"
                        checked={selectedForUnlink.length === allOccurrences.length && allOccurrences.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedForUnlink(allOccurrences.map(o => o.event_id));
                          } else {
                            setSelectedForUnlink([]);
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-[#1E3A5F]">Year</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#1E3A5F]">Event Title</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#1E3A5F]">Location</th>
                    <th className="text-right py-3 px-4 font-semibold text-[#1E3A5F]">Sales</th>
                    <th className="text-right py-3 px-4 font-semibold text-[#1E3A5F]">Booth Cost</th>
                    <th className="text-right py-3 px-4 font-semibold text-[#1E3A5F]">Profit</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#1E3A5F]">ROI</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#1E3A5F]">Rating</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#1E3A5F]">Notes</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {allOccurrences.sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((occ) => {
                    const profit = (occ.sales_total || 0) - (occ.booth_cost || 0);
                    const roi = (occ.booth_cost || 0) > 0 ? (profit / (occ.booth_cost || 1)) * 100 : null;
                    const isSelected = selectedForUnlink.includes(occ.event_id);
                    
                    const handleRowClick = (e) => {
                      // Don't navigate if clicking on checkbox
                      if (e.target.type === 'checkbox') return;
                      navigate(`/events/${occ.event_id}/edit`);
                    };
                    
                    return (
                      <tr 
                        key={occ.event_id} 
                        onClick={handleRowClick}
                        className={`border-b border-gray-100 hover:bg-[#1E3A5F]/10 transition-colors cursor-pointer ${isSelected ? 'bg-red-50' : ''}`}
                        data-testid={`series-row-${occ.event_id}`}
                      >
                        <td className="py-4 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedForUnlink([...selectedForUnlink, occ.event_id]);
                              } else {
                                setSelectedForUnlink(selectedForUnlink.filter(id => id !== occ.event_id));
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                            data-testid={`checkbox-${occ.event_id}`}
                          />
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-bold text-[#1E3A5F]">
                            {occ.date ? new Date(occ.date).getFullYear() : 'N/A'}
                          </span>
                        </td>
                        <td className="py-4 px-4 font-medium text-[#1E3A5F] hover:text-[#1E3A5F] hover:underline">
                          <span className="flex items-center gap-2">
                            {occ.title}
                            <Edit className="w-3 h-3 opacity-50" />
                          </span>
                        </td>
                        <td className="py-4 px-4 text-gray-500">
                          {occ.city && occ.state ? `${occ.city}, ${occ.state}` : '—'}
                        </td>
                        <td className="py-4 px-4 text-right font-semibold text-[#1E3A5F]">
                          {formatCurrency(occ.sales_total || 0)}
                        </td>
                        <td className="py-4 px-4 text-right text-gray-600">
                          {formatCurrency(occ.booth_cost || 0)}
                        </td>
                        <td className={`py-4 px-4 text-right font-semibold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(profit)}
                        </td>
                        <td className={`py-4 px-4 text-center font-semibold ${roi !== null && roi >= 0 ? 'text-green-600' : roi !== null ? 'text-red-600' : 'text-gray-400'}`}>
                          {roi !== null ? `${roi.toFixed(0)}%` : '—'}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex justify-center">{renderStars(occ.event_rating)}</div>
                        </td>
                        <td className="py-4 px-4 max-w-xs">
                          {occ.personal_notes ? (
                            <p className="text-xs text-gray-500 line-clamp-2">{occ.personal_notes}</p>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <Edit className="w-4 h-4 text-[#1E3A5F]" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Compact view for sidebar */
          allOccurrences.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Sales History
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {allOccurrences.sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((occ) => (
                  <Link
                    key={occ.event_id}
                    to={`/events/${occ.event_id}`}
                    className="block p-3 bg-gray-50 hover:bg-[#1E3A5F]/10 rounded-xl transition-colors"
                    data-testid={`occurrence-${occ.event_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-[#1E3A5F]">{occ.title}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(occ.date)}
                          </span>
                          {occ.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {occ.city}, {occ.state}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-[#1E3A5F]">{formatCurrency(occ.sales_total)}</p>
                        <div className="mt-1">{renderStars(occ.event_rating)}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 ml-2" />
                    </div>
                    
                    {/* Products sold summary */}
                    {occ.products_sold && occ.products_sold.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          Top products: {occ.products_sold.slice(0, 3).map(p => p.product_name).join(', ')}
                        </p>
                      </div>
                    )}
                    
                    {/* Personal notes preview */}
                    {occ.personal_notes && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500 flex items-start gap-1">
                          <FileText className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{occ.personal_notes}</span>
                        </p>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )
        )}

        {/* Buttons - Only show in compact mode */}
        {!fullWidth && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
            <Link to={`/sales?event_id=${eventId}&channel=event`}>
              <Button variant="outline" className="w-full border-2 border-[#1E3A5F] text-[#1E3A5F]" data-testid="add-sales-record-btn">
                <DollarSign className="w-4 h-4 mr-2" />
                Add Sales Record
              </Button>
            </Link>
            <Link to={`/add-event?series=${seriesHistory.id}&prefill=${eventId}`}>
              <Button className="w-full bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white" data-testid="add-event-in-series-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add New Event in Series
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  }

  // If no series - show suggestions and link options
  return (
    <div className={`bg-white rounded-2xl border-2 border-dashed border-[#1E3A5F]/50 p-6 ${fullWidth ? 'shadow-hard' : ''}`} data-testid="series-history-panel">
      <div className={`flex ${fullWidth ? 'flex-row items-start justify-between' : 'flex-col'} gap-6`}>
        <div className={`${fullWidth ? 'flex-1' : ''}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`${fullWidth ? 'w-14 h-14' : 'w-10 h-10'} rounded-xl bg-[#1E3A5F]/10 flex items-center justify-center`}>
              <History className={`${fullWidth ? 'w-7 h-7' : 'w-5 h-5'} text-[#1E3A5F]`} />
            </div>
            <div>
              <h3 className={`${fullWidth ? 'text-2xl' : 'text-lg'} font-bold text-[#1E3A5F]`}>Track This Event Series</h3>
              <p className="text-sm text-gray-500">Link to a series to track performance across multiple years</p>
            </div>
          </div>

          <p className={`text-gray-600 mb-6 ${fullWidth ? 'text-base max-w-2xl' : 'text-sm'}`}>
            Is this event part of a recurring series? Link it to track your sales history, compare year-over-year performance, and add notes for future reference. You can create a new series or link to an existing one.
          </p>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="mb-6">
              <p className={`font-medium text-gray-700 mb-3 ${fullWidth ? 'text-base' : 'text-sm'}`}>Suggested matches based on event name:</p>
              <div className={`${fullWidth ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : 'space-y-2'}`}>
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleLinkToSeries(s.id)}
                    disabled={linking}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-colors ${
                      s.isExact 
                        ? 'border-[#1E3A5F] bg-[#1E3A5F]/10 hover:bg-[#1E3A5F]/20' 
                        : 'border-gray-200 hover:border-[#1E3A5F] hover:bg-[#1E3A5F]/5'
                    }`}
                    data-testid={`suggestion-${s.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#1E3A5F]">{s.name}</p>
                        <p className="text-xs text-gray-500">
                          {s.occurrence_count} occurrence{s.occurrence_count !== 1 ? 's' : ''} • {formatCurrency(s.total_sales)} total sales
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {s.isExact && (
                          <span className="text-xs bg-[#1E3A5F] text-white px-2 py-0.5 rounded-full">
                            Best Match
                          </span>
                        )}
                        <Link2 className="w-4 h-4 text-[#1E3A5F]" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={`${fullWidth ? 'flex flex-col gap-3 min-w-[200px]' : 'flex gap-2'}`}>
          <Button
            variant="outline"
            className={`border-2 border-[#1E3A5F] text-[#1E3A5F] ${fullWidth ? 'w-full' : 'flex-1'}`}
            onClick={handleOpenLinkDialog}
            data-testid="link-to-series-btn"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Link to Existing Series
          </Button>
          <Button
            className={`bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white ${fullWidth ? 'w-full' : 'flex-1'}`}
            onClick={() => {
              setNewSeriesName(eventTitle.replace(/\s*(20\d{2}|'\d{2})\s*/gi, '').trim());
              setShowCreateDialog(true);
            }}
            data-testid="create-series-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Series
          </Button>
        </div>
      </div>

      {/* Link to Existing Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">Link to Event Series</DialogTitle>
            <DialogDescription>
              Select an existing series to link this event to.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {userSeries.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                You haven't created any series yet.
              </p>
            ) : (
              userSeries.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleLinkToSeries(s.id)}
                  disabled={linking}
                  className="w-full p-3 text-left rounded-xl border-2 border-gray-200 hover:border-[#1E3A5F] hover:bg-[#1E3A5F]/5 transition-colors"
                >
                  <p className="font-medium text-[#1E3A5F]">{s.name}</p>
                  <p className="text-xs text-gray-500">
                    {s.occurrence_count} occurrence{s.occurrence_count !== 1 ? 's' : ''} • {formatCurrency(s.total_sales)} total
                  </p>
                </button>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Series Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">Create Event Series</DialogTitle>
            <DialogDescription>
              Create a new series to track this recurring event across years.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="series-name">Series Name</Label>
              <Input
                id="series-name"
                value={newSeriesName}
                onChange={(e) => setNewSeriesName(e.target.value)}
                placeholder="e.g., Bluemont Fair"
                data-testid="series-name-input"
              />
              <p className="text-xs text-gray-500">
                Use a general name without the year (e.g., "Bluemont Fair" not "Bluemont Fair 2026")
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSeries}
              disabled={linking || !newSeriesName.trim()}
              className="bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white"
              data-testid="confirm-create-series-btn"
            >
              {linking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create & Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeriesHistoryPanel;
